/**
 * <sawtooth-border> — postage-stamp border around slotted content.
 *
 * Geometry (outside → in):
 *   1. Sawtooth teeth (protrude by `depth`)
 *   2. Solid border band (`border` thickness)
 *   3. Transparent content well (children keep their own background)
 *
 * A single SVG path draws both modes:
 *   fill   (default) — a filled strip per edge, from teeth to content well
 *   stroke — constant-width outline of the stamp silhouette
 *
 * `background` paints the full silhouette underneath either mode, so in stroke
 * mode it fills everything inside the outline.
 *
 * Each edge is fitted so it starts and ends on a full tooth (corners meet).
 * Preferred tooth/gap sizes are scaled just enough to land exactly on the edge.
 *
 * Attributes (px numbers, or CSS lengths):
 *   mode          — "fill" | "stroke" (default fill)
 *   edges         — which edges to render, e.g. "top bottom" (default all).
 *                   Skipped edges draw nothing and reserve no padding.
 *   color         — border/tooth/stroke color (default black)
 *   background    — fill behind the border, inside the silhouette
 *                   (default transparent)
 *   tooth         — preferred tooth length for all edges (default 10)
 *   gap           — preferred gap for all edges (default 8)
 *   tooth-x       — top/bottom tooth length (overrides `tooth`)
 *   gap-x         — top/bottom gap (overrides `gap`)
 *   tooth-y       — left/right tooth length (overrides `tooth`)
 *   gap-y         — left/right gap (overrides `gap`)
 *   depth         — how far teeth protrude outward (default 10)
 *   border        — solid band thickness between teeth and content (default 3)
 *   stroke-width  — outline thickness in stroke mode (default 1.5)
 *
 * @example
 *   <sawtooth-border mode="stroke" stroke-width="2" tooth-x="30" gap-x="10">
 *     <button type="submit">Add to cart</button>
 *   </sawtooth-border>
 *
 * <sawtooth-clip> — postage-stamp clip on light-DOM children (images).
 * Teeth cut into the element box; same tooth/gap fitter as the border.
 * Not a drawing primitive — do not wrap photos in <sawtooth-border>.
 *
 * `edges` limits which sides are toothed (the others stay straight).
 * Lengths and edges can also be set with CSS variables, so a media query
 * can change them: --sawtooth-tooth, --sawtooth-gap, --sawtooth-depth,
 * --sawtooth-edges. Attributes win when both are set.
 *
 * @example
 *   <sawtooth-clip edges="right" tooth-x="30" gap-x="10" depth="12">
 *     <img src="..." alt="">
 *   </sawtooth-clip>
 */

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * @param {string | null} value
 * @param {number} fallback
 * @returns {number} CSS px length
 */
function parseLength(value, fallback) {
  if (value == null || value === "") return fallback;
  const px = Number.parseFloat(String(value).trim());
  if (!Number.isFinite(px) || px < 0) return fallback;
  return px;
}

/** Off-document probe so rem/em lengths become px. Unitless numbers stay px. */
const lengthProbe = document.createElement("div");
lengthProbe.style.cssText =
  "position:absolute;visibility:hidden;pointer-events:none;top:0;left:0;";

/**
 * @param {string | null} value
 * @param {number} fallback
 * @returns {number}
 */
function resolveCssLength(value, fallback) {
  if (value == null || value === "") return fallback;
  const raw = String(value).trim();
  if (/^-?[\d.]+(px)?$/.test(raw)) return parseLength(raw, fallback);
  if (!lengthProbe.isConnected) document.documentElement.appendChild(lengthProbe);
  lengthProbe.style.width = raw;
  const px = lengthProbe.getBoundingClientRect().width;
  return Number.isFinite(px) && px >= 0 ? px : fallback;
}

/**
 * Fit tooth/gap so an edge of `length` starts and ends on a tooth:
 *   length = n * tooth + (n - 1) * gap   (n >= 2)
 *
 * @param {number} length
 * @param {number} tooth
 * @param {number} gap
 * @returns {{ tooth: number, gap: number, count: number }}
 */
function fitEdgePattern(length, tooth, gap) {
  const safeGap = Math.max(gap, 0);
  if (length <= 0 || tooth <= 0) return { tooth, gap: safeGap, count: 0 };

  const estimate = Math.round((length + safeGap) / (tooth + safeGap));

  /** @type {{ n: number, k: number } | null} */
  let best = null;
  for (let n = Math.max(2, estimate - 3); n <= estimate + 3; n++) {
    const natural = n * tooth + (n - 1) * safeGap;
    if (natural <= 0) continue;
    const k = length / natural;
    if (!best || Math.abs(k - 1) < Math.abs(best.k - 1)) best = { n, k };
  }
  if (!best) best = { n: 2, k: length / (2 * tooth + safeGap) };

  return { tooth: tooth * best.k, gap: safeGap * best.k, count: best.n };
}

/** Clockwise from the top-left corner. */
const EDGES = ["top", "right", "bottom", "left"];

/**
 * @param {string | null} value
 * @returns {Set<string>} edges to render
 */
function parseEdges(value) {
  if (value == null) return new Set(EDGES);
  const tokens = value.toLowerCase().split(/[\s,]+/).filter(Boolean);
  if (tokens.includes("all")) return new Set(EDGES);
  const edges = new Set(tokens.filter((token) => EDGES.includes(token)));
  if (edges.size === 0 && !tokens.includes("none")) return new Set(EDGES);
  return edges;
}

/**
 * Geometry helpers for one edge, expressed in "along the edge" /
 * "inward from the edge" coordinates so all four share the same walk.
 *
 * @param {string} edge
 * @param {{ width: number, height: number }} box
 */
function edgeAxis(edge, { width, height }) {
  switch (edge) {
    case "top":
      return { start: 0, end: width, direction: 1, point: (a, i) => [a, i] };
    case "right":
      return {
        start: 0,
        end: height,
        direction: 1,
        point: (a, i) => [width - i, a],
      };
    case "bottom":
      return {
        start: width,
        end: 0,
        direction: -1,
        point: (a, i) => [a, height - i],
      };
    default:
      return { start: height, end: 0, direction: -1, point: (a, i) => [i, a] };
  }
}

/**
 * Outer boundary of one edge, from its start corner to its end corner.
 * Teeth sit flush with the box edge; gaps recess inward by `depth`.
 *
 * @param {string} edge
 * @param {{ width: number, height: number, depth: number }} box
 * @param {{ tooth: number, gap: number, count: number } | null} pattern
 *   when null the edge is a straight line (no teeth)
 * @returns {[number, number][]}
 */
function edgePoints(edge, box, pattern) {
  const { start, end, direction, point } = edgeAxis(edge, box);
  // Always span corner to corner so callers can dedupe on the shared corner.
  if (!pattern) return [point(start, 0), point(end, 0)];

  const points = [point(start, 0)];
  let along = start;
  for (let i = 0; i < pattern.count; i++) {
    along += direction * pattern.tooth;
    points.push(point(along, 0));
    if (i === pattern.count - 1) break;
    points.push(point(along, box.depth));
    along += direction * pattern.gap;
    points.push(point(along, box.depth));
    points.push(point(along, 0));
  }
  return points;
}

/**
 * @param {[number, number][]} points
 * @param {boolean} close
 * @returns {string}
 */
function toPath(points, close) {
  if (points.length === 0) return "";
  const [[x, y], ...rest] = points;
  const commands = rest.map(([px, py]) => `L ${px} ${py}`);
  return `M ${x} ${y} ${commands.join(" ")}${close ? " Z" : ""}`;
}

/**
 * Chain consecutive edge boundaries into one outline, dropping the corner
 * point each edge shares with its predecessor.
 *
 * @param {string[]} edges
 * @param {{ width: number, height: number, depth: number }} box
 * @param {Record<string, { tooth: number, gap: number, count: number } | null>} patterns
 * @param {boolean} closed
 * @returns {string}
 */
function buildOutlinePath(edges, box, patterns, closed) {
  /** @type {[number, number][]} */
  const points = [];
  for (const edge of edges) {
    const next = edgePoints(edge, box, patterns[edge]);
    points.push(...(points.length ? next.slice(1) : next));
  }
  // A full loop ends back on its start point; Z draws that segment instead.
  if (closed) points.pop();
  return toPath(points, closed);
}

/**
 * A filled strip for one edge, spanning from its toothed outer boundary to
 * the content well. Adjacent strips overlap at the corners and union under
 * the nonzero fill rule.
 *
 * @param {string} edge
 * @param {{ width: number, height: number, depth: number }} box
 * @param {{ tooth: number, gap: number, count: number } | null} pattern
 * @param {number} inset distance from the box edge to the content well
 * @returns {string}
 */
function buildEdgeStripPath(edge, box, pattern, inset) {
  const { width, height } = box;
  /** @type {Record<string, [number, number][]>} */
  const returnLeg = {
    top: [
      [width, inset],
      [0, inset],
    ],
    right: [
      [width - inset, height],
      [width - inset, 0],
    ],
    bottom: [
      [0, height - inset],
      [width, height - inset],
    ],
    left: [
      [inset, 0],
      [inset, height],
    ],
  };
  return toPath([...edgePoints(edge, box, pattern), ...returnLeg[edge]], true);
}

/**
 * Split the rendered edges into runs of consecutive edges so a partial
 * outline is stroked as continuous polylines with mitered corners.
 *
 * @param {Set<string>} edges
 * @returns {{ edges: string[], closed: boolean }[]}
 */
function strokeRuns(edges) {
  if (edges.size === 0) return [];
  if (edges.size === EDGES.length) return [{ edges: [...EDGES], closed: true }];

  // Begin at an edge whose predecessor is missing, so no run wraps the end.
  const offset = EDGES.findIndex(
    (edge, i) => edges.has(edge) && !edges.has(EDGES[(i + 3) % 4]),
  );

  /** @type {{ edges: string[], closed: boolean }[]} */
  const runs = [];
  /** @type {string[]} */
  let run = [];
  for (let i = 0; i < EDGES.length; i++) {
    const edge = EDGES[(offset + i) % EDGES.length];
    if (edges.has(edge)) {
      run.push(edge);
    } else if (run.length) {
      runs.push({ edges: run, closed: false });
      run = [];
    }
  }
  if (run.length) runs.push({ edges: run, closed: false });
  return runs;
}

const STYLES = `
  :host {
    --sawtooth-color: #000;
    --sawtooth-background: transparent;
    --sawtooth-tooth: 10px;
    --sawtooth-gap: 8px;
    --sawtooth-tooth-x: var(--sawtooth-tooth);
    --sawtooth-gap-x: var(--sawtooth-gap);
    --sawtooth-tooth-y: var(--sawtooth-tooth);
    --sawtooth-gap-y: var(--sawtooth-gap);
    --sawtooth-depth: 10px;
    --sawtooth-border: 3px;
    --sawtooth-stroke-width: 1.5px;
    --sawtooth-inset: calc(var(--sawtooth-depth) + var(--sawtooth-border));

    display: inline-block;
    position: relative;
    /* Skipped edges reserve no space; JS zeroes their padding variable. */
    padding:
      var(--sawtooth-pad-top, var(--sawtooth-inset))
      var(--sawtooth-pad-right, var(--sawtooth-inset))
      var(--sawtooth-pad-bottom, var(--sawtooth-inset))
      var(--sawtooth-pad-left, var(--sawtooth-inset));
    box-sizing: border-box;
    vertical-align: middle;
    /*
      Do not set background here. The host defaults to transparent so the
      SVG silhouette shows through, and light-DOM background-color can
      still tint iOS Safari chrome (the :host background shorthand would
      win over that and reset background-clip).
    */
  }

  .frame {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    /* Stroke straddles the silhouette, so half of it lands outside the box. */
    overflow: visible;
    pointer-events: none;
  }

  /* Paints the whole silhouette beneath the border, including the well. */
  .frame__background {
    fill: var(--sawtooth-background);
    stroke: none;
  }

  /* Per-edge strips overlap at the corners; nonzero unions them. */
  .frame__path {
    fill: var(--sawtooth-color);
    fill-rule: nonzero;
    stroke: none;
  }

  :host([mode="stroke"]) .frame__path {
    fill: none;
    stroke: var(--sawtooth-color);
    stroke-width: var(--sawtooth-stroke-width);
    stroke-linejoin: miter;
    stroke-linecap: butt;
  }

  .inner {
    position: relative;
    z-index: 1;
    display: block;
  }

  ::slotted(*) {
    display: block;
    width: 100%;
    box-sizing: border-box;
  }
`;

class SawtoothBorder extends HTMLElement {
  static observedAttributes = [
    "mode",
    "edges",
    "color",
    "background",
    "tooth",
    "gap",
    "tooth-x",
    "gap-x",
    "tooth-y",
    "gap-y",
    "depth",
    "border",
    "stroke-width",
  ];

  /** @type {ResizeObserver | null} */
  #ro = null;

  /** @type {SVGSVGElement} */
  #svg;

  /** @type {SVGPathElement} */
  #backgroundPath;

  /** @type {SVGPathElement} */
  #path;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });

    const style = document.createElement("style");
    style.textContent = STYLES;

    this.#svg = document.createElementNS(SVG_NS, "svg");
    this.#svg.setAttribute("class", "frame");
    this.#svg.setAttribute("aria-hidden", "true");
    this.#svg.setAttribute("preserveAspectRatio", "none");

    this.#backgroundPath = document.createElementNS(SVG_NS, "path");
    this.#backgroundPath.setAttribute("class", "frame__background");

    this.#path = document.createElementNS(SVG_NS, "path");
    this.#path.setAttribute("class", "frame__path");
    this.#svg.append(this.#backgroundPath, this.#path);

    const inner = document.createElement("div");
    inner.className = "inner";
    inner.append(document.createElement("slot"));

    this.shadowRoot.append(style, this.#svg, inner);
  }

  connectedCallback() {
    this.#syncFromAttributes();
    this.#render();
    this.#ro = new ResizeObserver(() => this.#render());
    this.#ro.observe(this);
  }

  disconnectedCallback() {
    this.#ro?.disconnect();
    this.#ro = null;
  }

  /**
   * @param {string} name
   * @param {string | null} _old
   * @param {string | null} _value
   */
  attributeChangedCallback(name, _old, _value) {
    this.#syncFromAttributes();
    // Colors resolve through CSS variables; only geometry needs a new path.
    if (name !== "color" && name !== "background") this.#render();
  }

  /**
   * @param {string} attr
   * @param {string} cssVar
   * @param {number} fallback
   */
  #syncLengthAttr(attr, cssVar, fallback) {
    const value = this.getAttribute(attr);
    if (value == null) this.style.removeProperty(cssVar);
    else this.style.setProperty(cssVar, `${parseLength(value, fallback)}px`);
  }

  /**
   * @param {string} attr
   * @param {string} cssVar
   */
  #syncColorAttr(attr, cssVar) {
    const value = this.getAttribute(attr);
    if (value) this.style.setProperty(cssVar, value);
    else this.style.removeProperty(cssVar);
  }

  #syncFromAttributes() {
    this.#syncColorAttr("color", "--sawtooth-color");
    this.#syncColorAttr("background", "--sawtooth-background");

    const edges = parseEdges(this.getAttribute("edges"));
    for (const edge of EDGES) {
      const cssVar = `--sawtooth-pad-${edge}`;
      if (edges.has(edge)) this.style.removeProperty(cssVar);
      else this.style.setProperty(cssVar, "0px");
    }

    this.#syncLengthAttr("tooth", "--sawtooth-tooth", 10);
    this.#syncLengthAttr("gap", "--sawtooth-gap", 8);
    this.#syncLengthAttr("tooth-x", "--sawtooth-tooth-x", 10);
    this.#syncLengthAttr("gap-x", "--sawtooth-gap-x", 8);
    this.#syncLengthAttr("tooth-y", "--sawtooth-tooth-y", 10);
    this.#syncLengthAttr("gap-y", "--sawtooth-gap-y", 8);
    this.#syncLengthAttr("depth", "--sawtooth-depth", 10);
    this.#syncLengthAttr("border", "--sawtooth-border", 3);
    this.#syncLengthAttr("stroke-width", "--sawtooth-stroke-width", 1.5);
  }

  #render() {
    const { width, height } = this.getBoundingClientRect();
    if (width <= 0 || height <= 0) return;

    const cs = getComputedStyle(this);
    const read = (name, fallback) =>
      parseLength(cs.getPropertyValue(name), fallback);

    const depth = read("--sawtooth-depth", 10);
    const border = read("--sawtooth-border", 3);
    const box = { width, height, depth };

    const horizontal = fitEdgePattern(
      width,
      read("--sawtooth-tooth-x", 10),
      read("--sawtooth-gap-x", 8),
    );
    const vertical = fitEdgePattern(
      height,
      read("--sawtooth-tooth-y", 10),
      read("--sawtooth-gap-y", 8),
    );

    // Skipped edges get a null pattern, which draws them as a straight line.
    const edges = parseEdges(this.getAttribute("edges"));
    const patterns = Object.fromEntries(
      EDGES.map((edge) => [
        edge,
        edges.has(edge)
          ? edge === "top" || edge === "bottom"
            ? horizontal
            : vertical
          : null,
      ]),
    );

    this.#svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    this.#backgroundPath.setAttribute(
      "d",
      buildOutlinePath(EDGES, box, patterns, true),
    );

    const isStroke = this.getAttribute("mode")?.toLowerCase() === "stroke";
    const d = isStroke
      ? strokeRuns(edges)
          .map((run) => buildOutlinePath(run.edges, box, patterns, run.closed))
          .join(" ")
      : [...edges]
          .map((edge) =>
            buildEdgeStripPath(edge, box, patterns[edge], depth + border),
          )
          .join(" ");

    this.#path.setAttribute("d", d);
  }
}

if (!customElements.get("sawtooth-border")) {
  customElements.define("sawtooth-border", SawtoothBorder);
}

/**
 * Clip children to a 4-sided stamp silhouette. Teeth sit on the box edge;
 * gaps recede inward by `depth` — the inverse layout of <sawtooth-border>,
 * which pads content inside a drawn frame.
 */
class SawtoothClip extends HTMLElement {
  static observedAttributes = [
    "edges",
    "tooth",
    "gap",
    "tooth-x",
    "gap-x",
    "tooth-y",
    "gap-y",
    "depth",
  ];

  /** @type {ResizeObserver | null} */
  #ro = null;

  connectedCallback() {
    this.#syncFromAttributes();
    this.#render();
    this.#ro = new ResizeObserver(() => this.#render());
    this.#ro.observe(this);
    // Viewport changes can swap CSS variables without resizing this box.
    this.#ro.observe(document.documentElement);
  }

  disconnectedCallback() {
    this.#ro?.disconnect();
    this.#ro = null;
  }

  attributeChangedCallback() {
    this.#syncFromAttributes();
    this.#render();
  }

  /**
   * Attributes become inline px variables so they beat a stylesheet.
   * Missing attributes leave the variable alone for CSS (including media queries).
   */
  #syncFromAttributes() {
    const sync = (attr, cssVar, fallback) => {
      const value = this.getAttribute(attr);
      if (value == null) this.style.removeProperty(cssVar);
      else this.style.setProperty(cssVar, `${parseLength(value, fallback)}px`);
    };
    sync("tooth", "--sawtooth-tooth", 10);
    sync("gap", "--sawtooth-gap", 8);
    sync("tooth-x", "--sawtooth-tooth-x", 10);
    sync("gap-x", "--sawtooth-gap-x", 8);
    sync("tooth-y", "--sawtooth-tooth-y", 10);
    sync("gap-y", "--sawtooth-gap-y", 8);
    sync("depth", "--sawtooth-depth", 10);
  }

  /**
   * @param {CSSStyleDeclaration} cs
   * @param {string} cssVar
   * @param {number} fallback
   */
  #length(cs, cssVar, fallback) {
    return resolveCssLength(cs.getPropertyValue(cssVar), fallback);
  }

  #render() {
    const { width, height } = this.getBoundingClientRect();
    if (width <= 0 || height <= 0) return;

    const cs = getComputedStyle(this);
    const depth = this.#length(cs, "--sawtooth-depth", 10);
    const tooth = this.#length(cs, "--sawtooth-tooth", 10);
    const gap = this.#length(cs, "--sawtooth-gap", 8);
    const box = { width, height, depth };

    const horizontal = fitEdgePattern(
      width,
      this.#length(cs, "--sawtooth-tooth-x", tooth),
      this.#length(cs, "--sawtooth-gap-x", gap),
    );
    const vertical = fitEdgePattern(
      height,
      this.#length(cs, "--sawtooth-tooth-y", tooth),
      this.#length(cs, "--sawtooth-gap-y", gap),
    );

    const edgesValue =
      cs.getPropertyValue("--sawtooth-edges").trim() || this.getAttribute("edges");
    const edges = parseEdges(edgesValue);
    const patterns = Object.fromEntries(
      EDGES.map((edge) => [
        edge,
        edges.has(edge)
          ? edge === "top" || edge === "bottom"
            ? horizontal
            : vertical
          : null,
      ]),
    );

    const d = buildOutlinePath(EDGES, box, patterns, true);
    this.style.clipPath = `path('${d}')`;
  }
}

if (!customElements.get("sawtooth-clip")) {
  customElements.define("sawtooth-clip", SawtoothClip);
}
